import { takeEvery, put, select, call, spawn } from 'redux-saga/effects';
import { matchPath } from 'react-router';
import { LOCATION_CHANGE, replace } from 'connected-react-router';
import { application as applicationActions, router as routerActions, flyers as flyersActions, viewer as viewerActions } from 'flux/actions';
import { flyerPath, flyersPath, tagPath, flyerBasePathWithoutPage, allPaths } from 'app/routes';
import { property as propertySelectors, router as routerSelectors, flyers as flyersSelectors, flyerGibs as flyerGibsSelectors } from 'flux/selectors';
import { google as googleApi } from 'flux/api';
import pathToRegexp from 'path-to-regexp';
import { createLinkPath, formatGeolocation, parseLocationSearch } from 'utils';
import errorHandler from 'errorHandler';

export function* updateRouterState(location) {
  const query = parseLocationSearch(location);
  yield put(routerActions.updateQuery(query));

  // Pinchable Viewer
  const { pinchableViewer } = query;
  if (pinchableViewer) {
    // This will transform 'true' or 'false' in relative Booleans
    const isViewerPinchable = JSON.parse(pinchableViewer);
    yield put(applicationActions.setPinchableViewer(isViewerPinchable));
  }

  // Router geolocation

  const { lat = null, lng = null } = query;
  if (lat && lng) {
    const parsedLocation = {
      lat: parseFloat(lat),
      lng: parseFloat(lng),
    };
    try {
      yield put(applicationActions.setLoadingGeolocation(true));
      const label = yield call(googleApi.geocode, parsedLocation);
      const geolocation = formatGeolocation({ lat, lng, label }, 'queryString');
      yield put(routerActions.updateGeolocation(geolocation));
    } catch (e) {
      errorHandler(e);
    } finally {
      yield put(applicationActions.setLoadingGeolocation(false));
      yield put(routerActions.setGclidDependenciesReady());
    }
  } else {
    yield put(routerActions.setGclidDependenciesReady());
  }
}

// Redirect in case no route is found
export function* onLocationChange({ payload }) {
  const { location } = payload;
  const { pathname, search } = location;
  const redirect = allPaths.every((path) => !matchPath(pathname, { path, exact: true }));

  if (redirect) {
    const flyerWithoutPageMatch = matchPath(pathname, {
      path: flyerBasePathWithoutPage,
      exact: true,
    });
    const pathTo = pathToRegexp.compile(`${flyerBasePathWithoutPage}/1`);
    let replacePath = flyerWithoutPageMatch ? pathTo(flyerWithoutPageMatch.params) : createLinkPath(flyersPath);
    if (!replacePath.includes(search)) {
      replacePath += search;
    }
    yield put(replace(replacePath));
  } else {
    yield spawn(updateRouterState, location);
  }
}

export function* onViewerEnrichment({ payload }) {
  const { id: tagId, flyerGibType } = payload;
  const currentFlyerId = yield select(flyersSelectors.selectCurrentFlyerId);

  const propertyId = yield select(propertySelectors.selectPropertyId);
  const flyerGib = yield select(flyerGibsSelectors.selectFlyerGib, tagId);

  const isLeroyMerlin = ['62307978-32e8-47f0-b384-504bac1f35de', '5da49f3e-2948-4250-b4e4-0659ac1f5ee2'].includes(propertyId);

  if (isLeroyMerlin && flyerGib?.settings?.layout?.buttons) {
    const findExternalCta = flyerGib.settings.layout.buttons.find((btn) => !!btn?.attributes?.href);
    if (findExternalCta) {
      window.open(findExternalCta.attributes.href);
      return;
    }
  }

  if (flyerGibType !== 'external_link') {
    yield put(replace(createLinkPath(tagPath, { flyerId: currentFlyerId, tagId })));
  }
}

export function* onViewerBrowse({ payload }) {
  const { currentPages } = payload;
  const isFlyerPath = yield select(routerSelectors.routerMatchPath, flyerPath);
  if (isFlyerPath) {
    const flyerPage = currentPages[0];
    if (flyerPage === undefined) return;
    const flyerId = yield select(flyersSelectors.selectCurrentFlyerId);
    yield put(replace(createLinkPath(flyerPath, { flyerId, flyerPage })));
  }
}

export default function* () {
  yield takeEvery(LOCATION_CHANGE, onLocationChange);
  yield takeEvery(viewerActions.types.ENRICHMENT, onViewerEnrichment);
  yield takeEvery(viewerActions.types.BROWSE, onViewerBrowse);
}
